import React, { useCallback } from 'react'
import { l, Modal } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { KnowledgeEditorSource } from 'apps/aiAutomation/components/KnowledgeEditor/lib/types'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import { AiAlwaysOnAutomationsSelectors } from 'apps/aiAutomation/store/core'
import {
  AiAutomationKnowledgeActions,
  AiAutomationKnowledgeSelectors,
} from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'

interface DeleteConfirmationProps {
  onClose: () => void
  id: string
  source: KnowledgeEditorSource
}

export const KnowledgeEntryDeleteModal = ({ onClose, id, source }: DeleteConfirmationProps) => {
  const dispatch = useAppDispatch()

  const entries = useAppSelector(AiAutomationKnowledgeSelectors.getList)
  const isAutomationLive = useAppSelector(AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus)
  const isDeleting = useAppSelector((state) =>
    AiAutomationKnowledgeSelectors.getIsItemDeleting(state, id),
  )

  const automationWillBeStopped = isAutomationLive && entries.length === 1

  const handleDelete = useCallback(async () => {
    await dispatch(AiAutomationKnowledgeActions.removeEntryFx(id)).unwrap()
    AiAutomationAnalytics.logKnowledgeEntryDeleted({ id, source })

    onClose()

    dispatch(AiAutomationLayoutActions.sidebarClosed())
  }, [dispatch, id, source, onClose])

  return (
    <Modal
      width={460}
      open
      onRequestClose={onClose}
      buttons={[
        {
          onClick: onClose,
          label: l.translate('Cancel'),
          ['data-test-id']: 'ai-flow/knowledge-editor-delete-modal-close-btn',
        },
        {
          onClick: handleDelete,
          variant: 'danger',
          label: l.translate('Delete'),
          ['data-test-id']: 'ai-flow/knowledge-editor-delete-modal-confirm-btn',
          loading: isDeleting,
        },
      ]}
      title={
        automationWillBeStopped
          ? l.translate('Delete and stop Instant Answers?')
          : l.translate('Delete this information?')
      }
    >
      <p className="p-x-xs p-y">
        {!isAutomationLive &&
          l.translate(
            'This will remove the information from your knowledge base. AI won’t use it when you go live unless you re-add it later.',
          )}
        {automationWillBeStopped &&
          l.translate(
            'If you delete this entry, AI won’t have any info to answer  so the automation will stop.',
          )}
        {isAutomationLive &&
          entries.length > 1 &&
          l.translate('AI will no longer answer questions related to this information')}
      </p>
    </Modal>
  )
}
